import React, { useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Calendar as CalendarIcon, Repeat, X } from "lucide-react";
import { TaskPriority, RecurrencePattern, RecurrenceFrequency, RecurrenceEndType } from "./types";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format, startOfDay, isBefore } from "date-fns";

import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { AssigneeSelector } from "./AssigneeSelector";
import { useWorkspace } from "@/context/WorkspaceContext";
import { useAuth } from "@/context/AuthContext";
import { fetchWorkspaceMembers, WorkspaceMemberWithProfile } from "@/lib/workspaceMembers";
interface NewTaskDialogProps {
  onAdd: (data: {
    title: string;
    description?: string;
    priority: TaskPriority;
    startDate?: string;
    dueDate?: string;
    assignees?: { id: string; name: string; email: string; avatar?: string }[];
    targetColumnId?: string;
    isRecurring?: boolean;
    recurrencePattern?: RecurrencePattern;
  }) => void;

  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  hideTrigger?: boolean;
  targetColumnId?: string;
}

export const NewTaskDialog: React.FC<NewTaskDialogProps> = ({ onAdd, open: controlledOpen, onOpenChange, hideTrigger = false, targetColumnId }) => {
  const { currentWorkspace } = useWorkspace();
  const { user } = useAuth();
  const [internalOpen, setInternalOpen] = React.useState(false);

  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = onOpenChange !== undefined ? onOpenChange : setInternalOpen;
  const [title, setTitle] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [priority, setPriority] = React.useState<TaskPriority>("medium");
  const [startDate, setStartDate] = React.useState<Date | undefined>(undefined);
  const [dueDate, setDueDate] = React.useState<Date | undefined>(undefined);
  const [, setImageFile] = React.useState<File | null>(null);
  const [assignees, setAssignees] = React.useState<{ id: string; name: string; email: string; avatar?: string }[]>([]);
  const [teamMembers, setTeamMembers] = React.useState<WorkspaceMemberWithProfile[]>([]);

  // Recurring task state
  const [isRecurring, setIsRecurring] = React.useState(false);
  const [recurrenceFrequency, setRecurrenceFrequency] = React.useState<RecurrenceFrequency>("daily");
  const [recurrenceInterval, setRecurrenceInterval] = React.useState(1);
  const [recurrenceDaysOfWeek, setRecurrenceDaysOfWeek] = React.useState<number[]>([]);
  const [recurrenceDayOfMonth, setRecurrenceDayOfMonth] = React.useState<number>(1);
  const [recurrenceEndType, setRecurrenceEndType] = React.useState<RecurrenceEndType>("never");
  const [recurrenceOccurrences, setRecurrenceOccurrences] = React.useState(10);
  const [recurrenceEndDate, setRecurrenceEndDate] = React.useState<Date | undefined>(undefined);
  const [recurrencePopoverOpen, setRecurrencePopoverOpen] = React.useState(false);

  const handleAssigneesChange = (newAssignees: { id: string; name: string; email: string; avatar?: string }[]) => {
    setAssignees(newAssignees);
  };

  const getRecurrenceSummary = () => {
    if (!isRecurring) return null;

    let summary = `Every ${recurrenceInterval > 1 ? recurrenceInterval + ' ' : ''}${recurrenceFrequency}${recurrenceInterval > 1 && recurrenceFrequency !== 'daily' ? 's' : ''}`;

    if (recurrenceFrequency === 'weekly' && recurrenceDaysOfWeek.length > 0) {
      const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      const days = recurrenceDaysOfWeek.map(d => dayNames[d]).join(', ');
      summary += ` on ${days}`;
    }

    if (recurrenceFrequency === 'monthly' && recurrenceDayOfMonth) {
      summary += ` on day ${recurrenceDayOfMonth}`;
    }

    return summary;
  };

  // Fetch workspace members when dialog opens
  useEffect(() => {
    if (open && currentWorkspace) {
      fetchWorkspaceMembers(currentWorkspace.id)
        .then(members => {
          setTeamMembers(members);
        })
        .catch(error => {
          console.error('Error fetching workspace members:', error);
        });
    }
  }, [open, currentWorkspace]);
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    if (startDate && dueDate && dueDate < startDate) {
      return;
    }

    let recurrencePattern: RecurrencePattern | undefined = undefined;
    if (isRecurring) {
      recurrencePattern = {
        frequency: recurrenceFrequency,
        interval: recurrenceInterval,
        endType: recurrenceEndType,
      };

      if (recurrenceFrequency === "weekly" && recurrenceDaysOfWeek.length > 0) {
        recurrencePattern.daysOfWeek = recurrenceDaysOfWeek;
      }

      if (recurrenceFrequency === "monthly") {
        recurrencePattern.dayOfMonth = recurrenceDayOfMonth;
      }

      if (recurrenceEndType === "after") {
        recurrencePattern.occurrences = recurrenceOccurrences;
      } else if (recurrenceEndType === "on_date" && recurrenceEndDate) {
        recurrencePattern.endDate = recurrenceEndDate.toISOString();
      }
    }

    onAdd({
      title: title.trim(),
      description: description.trim() || undefined,
      priority,
      startDate: startDate ? startDate.toISOString() : undefined,
      dueDate: dueDate ? dueDate.toISOString() : undefined,
      assignees,
      targetColumnId,
      isRecurring,
      recurrencePattern,
    });

    // Reset form
    setTitle("");
    setDescription("");
    setPriority("medium");
    setStartDate(undefined);
    setDueDate(undefined);
    setImageFile(null);
    setAssignees([]);
    setIsRecurring(false);
    setRecurrenceFrequency("daily");
    setRecurrenceInterval(1);
    setRecurrenceDaysOfWeek([]);
    setRecurrenceDayOfMonth(1);
    setRecurrenceEndType("never");
    setRecurrenceOccurrences(10);
    setRecurrenceEndDate(undefined);
    setOpen(false);
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {!hideTrigger && (
        <DialogTrigger asChild>
          <Button className="bg-workspace-accent text-white hover:bg-workspace-accent-hover">
            <Plus className="mr-1 h-4 w-4" /> Add task
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="max-w-2xl max-h-[90vh] md:max-h-[90vh] max-md:max-w-full max-md:w-full max-md:h-full max-md:max-h-screen max-md:rounded-none max-md:inset-0 max-md:translate-x-0 max-md:translate-y-0 max-md:top-0 max-md:left-0 bg-card text-card-foreground border-border border overflow-hidden flex flex-col">
        <DialogHeader className="flex-shrink-0 max-md:border-b max-md:border-border max-md:pb-3">
          <DialogTitle className="text-xl font-semibold text-foreground">Create a new task</DialogTitle>
          <DialogDescription className="sr-only">Create a new task</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="flex-1 overflow-y-auto px-1 space-y-5 max-md:px-4">
          {/* Title */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground" htmlFor="title">
              Title <span className="text-destructive">*</span>
            </label>
            <Input
              className="bg-background border-input text-foreground placeholder:text-muted-foreground h-10 focus:outline-none focus-visible:outline-none focus-visible:ring-0"
              id="title"
              placeholder="Write a clear, concise title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground" htmlFor="description">Description</label>
            <Textarea
              className="bg-background border-input text-foreground placeholder:text-muted-foreground min-h-[80px] resize-none focus:outline-none focus-visible:outline-none focus-visible:ring-0"
              id="description"
              placeholder="Add details, links, or acceptance criteria"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          {/* Image Upload - Hidden by default, can be shown via toggle */}
          <div className="space-y-2 hidden">
            <label className="text-sm font-medium text-foreground" htmlFor="image">Cover Image</label>
            <Input
              className="bg-background border-input text-foreground placeholder:text-muted-foreground"
              id="image"
              type="file"
              accept="image/*"
              onChange={(e) => setImageFile(e.target.files?.[0] ?? null)}
            />
          </div>
          {/* Priority and Due Date */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Priority</label>
              <Select value={priority} onValueChange={(v) => setPriority(v as TaskPriority)}>
                <SelectTrigger className="bg-background border-input text-foreground h-10 focus:outline-none focus-visible:outline-none focus-visible:ring-0">
                  <SelectValue placeholder="Select priority" />
                </SelectTrigger>
                <SelectContent className="bg-popover border border-border text-foreground">
                  <SelectItem value="low">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-secondary" />
                      <span>Low</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="medium">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-info" />
                      <span>Medium</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="high">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-warning" />
                      <span>High</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="critical">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-destructive" />
                      <span>Critical</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Due date</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full h-10 justify-start text-left font-normal bg-background border-input text-foreground",
                      !dueDate && 'text-muted-foreground'
                    )}
                    type="button"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {dueDate ? format(dueDate, "MMM d, yyyy") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-popover border border-border text-foreground" align="start">
                  <Calendar
                    mode="single"
                    selected={dueDate}
                    onSelect={setDueDate}
                    disabled={(date) => isBefore(date, startOfDay(new Date()))}
                    initialFocus
                    className="pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
          {/* Assignees and Repeat Side by Side */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Assignees */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Assignees</label>
              <div className="space-y-3">
                <AssigneeSelector
                  currentAssignees={assignees}
                  onAssigneesChange={handleAssigneesChange}
                  teamMembers={teamMembers}
                  dropdownDirection="top"
                  excludeUserIds={user?.id ? [user.id] : []}
                />

                {assignees.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {assignees.map((assignee) => (
                      <div
                        key={assignee.id}
                        className="flex items-center gap-2 bg-accent/50 border border-border rounded-full px-3 py-1.5 group hover:bg-accent transition-colors"
                      >
                        <div className="h-5 w-5 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center text-[10px] font-medium text-primary">
                          {assignee.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                        </div>
                        <span className="text-xs font-medium text-foreground">
                          {assignee.name}
                        </span>
                        <button
                          type="button"
                          className="h-4 w-4 rounded-full flex items-center justify-center hover:bg-destructive/20 text-muted-foreground hover:text-destructive transition-colors ml-1"
                          onClick={() => {
                            const updatedAssignees = assignees.filter(a => a.id !== assignee.id);
                            handleAssigneesChange(updatedAssignees);
                          }}
                        >
                          <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Repeat */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Repeat</label>
              <div className="space-y-3">
                {!isRecurring ? (
                  <Popover open={recurrencePopoverOpen} onOpenChange={setRecurrencePopoverOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="default"
                        type="button"
                        size="sm"
                        className="bg-workspace-accent text-white hover:bg-workspace-accent-hover"
                        onClick={() => {
                          setIsRecurring(true);
                          setRecurrencePopoverOpen(true);
                        }}
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Add
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-80 p-0 bg-popover border-border text-foreground" align="start">
                      <div className="p-4 space-y-4">
                        <div className="flex items-center justify-between pb-3 border-b border-border">
                          <h4 className="font-semibold text-sm text-foreground">Repeat Settings</h4>
                          <Button
                            variant="ghost"
                            size="sm"
                            type="button"
                            className="h-6 w-6 p-0"
                            onClick={() => setRecurrencePopoverOpen(false)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>

                        <>
                            {/* Frequency */}
                            <div className="space-y-2">
                              <Label className="text-sm font-medium text-foreground">Repeat every</Label>
                              <div className="flex gap-2">
                                <Input
                                  type="number"
                                  min="1"
                                  max="365"
                                  value={recurrenceInterval}
                                  onChange={(e) => setRecurrenceInterval(parseInt(e.target.value) || 1)}
                                  className="bg-background border-input text-foreground w-16 h-9 focus:outline-none focus-visible:outline-none focus-visible:ring-0"
                                />
                                <Select value={recurrenceFrequency} onValueChange={(v) => setRecurrenceFrequency(v as RecurrenceFrequency)}>
                                  <SelectTrigger className="bg-background border-input text-foreground flex-1 h-9 focus:outline-none focus-visible:outline-none focus-visible:ring-0">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent className="bg-popover border border-border text-foreground">
                                    <SelectItem value="daily">Day{recurrenceInterval > 1 ? 's' : ''}</SelectItem>
                                    <SelectItem value="weekly">Week{recurrenceInterval > 1 ? 's' : ''}</SelectItem>
                                    <SelectItem value="monthly">Month{recurrenceInterval > 1 ? 's' : ''}</SelectItem>
                                    <SelectItem value="yearly">Year{recurrenceInterval > 1 ? 's' : ''}</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>

                            {/* Days of week */}
                            {recurrenceFrequency === "weekly" && (
                              <div className="space-y-2">
                                <Label className="text-sm font-medium text-foreground">Repeat on</Label>
                                <div className="grid grid-cols-7 gap-1">
                                  {[
                                    { label: "S", value: 0 },
                                    { label: "M", value: 1 },
                                    { label: "T", value: 2 },
                                    { label: "W", value: 3 },
                                    { label: "T", value: 4 },
                                    { label: "F", value: 5 },
                                    { label: "S", value: 6 },
                                  ].map((day) => (
                                    <label
                                      key={day.value}
                                      className={cn(
                                        "flex items-center justify-center h-8 rounded border cursor-pointer text-xs font-medium transition-colors",
                                        recurrenceDaysOfWeek.includes(day.value)
                                          ? "bg-primary text-primary-foreground border-primary"
                                          : "bg-background border-input text-muted-foreground hover:bg-accent"
                                      )}
                                    >
                                      <input
                                        type="checkbox"
                                        className="sr-only"
                                        checked={recurrenceDaysOfWeek.includes(day.value)}
                                        onChange={(e) => {
                                          if (e.target.checked) {
                                            setRecurrenceDaysOfWeek([...recurrenceDaysOfWeek, day.value].sort());
                                          } else {
                                            setRecurrenceDaysOfWeek(recurrenceDaysOfWeek.filter(d => d !== day.value));
                                          }
                                        }}
                                      />
                                      {day.label}
                                    </label>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Day of month */}
                            {recurrenceFrequency === "monthly" && (
                              <div className="space-y-2">
                                <Label className="text-sm font-medium text-foreground">Day of month</Label>
                                <Input
                                  type="number"
                                  min="1"
                                  max="31"
                                  value={recurrenceDayOfMonth}
                                  onChange={(e) => setRecurrenceDayOfMonth(parseInt(e.target.value) || 1)}
                                  className="bg-background border-input text-foreground w-20 h-9 focus:outline-none focus-visible:outline-none focus-visible:ring-0"
                                />
                              </div>
                            )}

                            {/* End options */}
                            <div className="space-y-2">
                              <Label className="text-sm font-medium text-foreground">Ends</Label>
                              <div className="space-y-1.5">
                                <label className="flex items-center gap-2 text-sm cursor-pointer">
                                  <input
                                    type="radio"
                                    name="end-type-new"
                                    checked={recurrenceEndType === "never"}
                                    onChange={() => setRecurrenceEndType("never")}
                                    className="h-3.5 w-3.5"
                                  />
                                  <span className="text-foreground">Never</span>
                                </label>

                                <label className="flex items-center gap-2 text-sm cursor-pointer">
                                  <input
                                    type="radio"
                                    name="end-type-new"
                                    checked={recurrenceEndType === "after"}
                                    onChange={() => setRecurrenceEndType("after")}
                                    className="h-3.5 w-3.5"
                                  />
                                  <span className="text-foreground">After</span>
                                  {recurrenceEndType === "after" && (
                                    <>
                                      <Input
                                        type="number"
                                        min="1"
                                        max="999"
                                        value={recurrenceOccurrences}
                                        onChange={(e) => setRecurrenceOccurrences(parseInt(e.target.value) || 1)}
                                        className="bg-background border-input text-foreground w-16 h-7 text-xs focus:outline-none focus-visible:outline-none focus-visible:ring-0"
                                        onClick={(e) => e.stopPropagation()}
                                      />
                                      <span className="text-xs text-muted-foreground">times</span>
                                    </>
                                  )}
                                </label>

                                <label className="flex items-center gap-2 text-sm cursor-pointer">
                                  <input
                                    type="radio"
                                    name="end-type-new"
                                    checked={recurrenceEndType === "on_date"}
                                    onChange={() => setRecurrenceEndType("on_date")}
                                    className="h-3.5 w-3.5"
                                  />
                                  <span className="text-foreground">On date</span>
                                </label>
                                {recurrenceEndType === "on_date" && (
                                  <div className="ml-6">
                                    <Popover>
                                      <PopoverTrigger asChild>
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          type="button"
                                          className={cn(
                                            "h-7 text-xs font-normal bg-background border-input",
                                            !recurrenceEndDate && 'text-muted-foreground'
                                          )}
                                        >
                                          <CalendarIcon className="mr-1 h-3 w-3" />
                                          {recurrenceEndDate ? format(recurrenceEndDate, "MMM d, yyyy") : "Pick date"}
                                        </Button>
                                      </PopoverTrigger>
                                      <PopoverContent className="w-auto p-0 bg-popover border border-border" align="start">
                                        <Calendar
                                          mode="single"
                                          selected={recurrenceEndDate}
                                          onSelect={setRecurrenceEndDate}
                                          disabled={(date) => isBefore(date, startOfDay(new Date()))}
                                          initialFocus
                                        />
                                      </PopoverContent>
                                    </Popover>
                                  </div>
                                )}
                              </div>
                            </div>

                            <div className="pt-3 border-t border-border flex gap-2">
                              <Button
                                size="sm"
                                type="button"
                                className="flex-1 h-8"
                                onClick={() => setRecurrencePopoverOpen(false)}
                              >
                                Done
                              </Button>
                            </div>
                          </>
                      </div>
                    </PopoverContent>
                  </Popover>
                ) : (
                  <Popover open={recurrencePopoverOpen} onOpenChange={setRecurrencePopoverOpen}>
                    <PopoverTrigger asChild>
                      <div className="flex items-center gap-2 bg-accent/50 border border-border rounded-lg px-3 py-2 group hover:bg-accent transition-colors cursor-pointer">
                        <Repeat className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                        <span className="text-xs font-medium text-foreground flex-1">
                          {getRecurrenceSummary()}
                        </span>
                        <button
                          type="button"
                          className="h-4 w-4 rounded-full flex items-center justify-center hover:bg-destructive/20 text-muted-foreground hover:text-destructive transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            setIsRecurring(false);
                            setRecurrenceDaysOfWeek([]);
                          }}
                        >
                          <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    </PopoverTrigger>
                    <PopoverContent className="w-80 p-0 bg-popover border-border text-foreground" align="start">
                      <div className="p-4 space-y-4">
                        <div className="flex items-center justify-between pb-3 border-b border-border">
                          <h4 className="font-semibold text-sm text-foreground">Repeat Settings</h4>
                          <Button
                            variant="ghost"
                            size="sm"
                            type="button"
                            className="h-6 w-6 p-0"
                            onClick={() => setRecurrencePopoverOpen(false)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>

                        {/* Frequency */}
                        <div className="space-y-2">
                          <Label className="text-sm font-medium text-foreground">Repeat every</Label>
                          <div className="flex gap-2">
                            <Input
                              type="number"
                              min="1"
                              max="365"
                              value={recurrenceInterval}
                              onChange={(e) => setRecurrenceInterval(parseInt(e.target.value) || 1)}
                              className="bg-background border-input text-foreground w-16 h-9 focus:outline-none focus-visible:outline-none focus-visible:ring-0"
                            />
                            <Select value={recurrenceFrequency} onValueChange={(v) => setRecurrenceFrequency(v as RecurrenceFrequency)}>
                              <SelectTrigger className="bg-background border-input text-foreground flex-1 h-9 focus:outline-none focus-visible:outline-none focus-visible:ring-0">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-popover border border-border text-foreground">
                                <SelectItem value="daily">Day{recurrenceInterval > 1 ? 's' : ''}</SelectItem>
                                <SelectItem value="weekly">Week{recurrenceInterval > 1 ? 's' : ''}</SelectItem>
                                <SelectItem value="monthly">Month{recurrenceInterval > 1 ? 's' : ''}</SelectItem>
                                <SelectItem value="yearly">Year{recurrenceInterval > 1 ? 's' : ''}</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        {/* Days of week */}
                        {recurrenceFrequency === "weekly" && (
                          <div className="space-y-2">
                            <Label className="text-sm font-medium text-foreground">Repeat on</Label>
                            <div className="grid grid-cols-7 gap-1">
                              {[
                                { label: "S", value: 0 },
                                { label: "M", value: 1 },
                                { label: "T", value: 2 },
                                { label: "W", value: 3 },
                                { label: "T", value: 4 },
                                { label: "F", value: 5 },
                                { label: "S", value: 6 },
                              ].map((day) => (
                                <label
                                  key={day.value}
                                  className={cn(
                                    "flex items-center justify-center h-8 rounded border cursor-pointer text-xs font-medium transition-colors",
                                    recurrenceDaysOfWeek.includes(day.value)
                                      ? "bg-primary text-primary-foreground border-primary"
                                      : "bg-background border-input text-muted-foreground hover:bg-accent"
                                  )}
                                >
                                  <input
                                    type="checkbox"
                                    className="sr-only"
                                    checked={recurrenceDaysOfWeek.includes(day.value)}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setRecurrenceDaysOfWeek([...recurrenceDaysOfWeek, day.value].sort());
                                      } else {
                                        setRecurrenceDaysOfWeek(recurrenceDaysOfWeek.filter(d => d !== day.value));
                                      }
                                    }}
                                  />
                                  {day.label}
                                </label>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Day of month */}
                        {recurrenceFrequency === "monthly" && (
                          <div className="space-y-2">
                            <Label className="text-sm font-medium text-foreground">Day of month</Label>
                            <Input
                              type="number"
                              min="1"
                              max="31"
                              value={recurrenceDayOfMonth}
                              onChange={(e) => setRecurrenceDayOfMonth(parseInt(e.target.value) || 1)}
                              className="bg-background border-input text-foreground w-20 h-9 focus:outline-none focus-visible:outline-none focus-visible:ring-0"
                            />
                          </div>
                        )}

                        {/* End options */}
                        <div className="space-y-2">
                          <Label className="text-sm font-medium text-foreground">Ends</Label>
                          <div className="space-y-1.5">
                            <label className="flex items-center gap-2 text-sm cursor-pointer">
                              <input
                                type="radio"
                                name="end-type-edit"
                                checked={recurrenceEndType === "never"}
                                onChange={() => setRecurrenceEndType("never")}
                                className="h-3.5 w-3.5"
                              />
                              <span className="text-foreground">Never</span>
                            </label>

                            <label className="flex items-center gap-2 text-sm cursor-pointer">
                              <input
                                type="radio"
                                name="end-type-edit"
                                checked={recurrenceEndType === "after"}
                                onChange={() => setRecurrenceEndType("after")}
                                className="h-3.5 w-3.5"
                              />
                              <span className="text-foreground">After</span>
                              {recurrenceEndType === "after" && (
                                <>
                                  <Input
                                    type="number"
                                    min="1"
                                    max="999"
                                    value={recurrenceOccurrences}
                                    onChange={(e) => setRecurrenceOccurrences(parseInt(e.target.value) || 1)}
                                    className="bg-background border-input text-foreground w-16 h-7 text-xs focus:outline-none focus-visible:outline-none focus-visible:ring-0"
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                  <span className="text-xs text-muted-foreground">times</span>
                                </>
                              )}
                            </label>

                            <label className="flex items-center gap-2 text-sm cursor-pointer">
                              <input
                                type="radio"
                                name="end-type-edit"
                                checked={recurrenceEndType === "on_date"}
                                onChange={() => setRecurrenceEndType("on_date")}
                                className="h-3.5 w-3.5"
                              />
                              <span className="text-foreground">On date</span>
                            </label>
                            {recurrenceEndType === "on_date" && (
                              <div className="ml-6">
                                <Popover>
                                  <PopoverTrigger asChild>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      type="button"
                                      className={cn(
                                        "h-7 text-xs font-normal bg-background border-input",
                                        !recurrenceEndDate && 'text-muted-foreground'
                                      )}
                                    >
                                      <CalendarIcon className="mr-1 h-3 w-3" />
                                      {recurrenceEndDate ? format(recurrenceEndDate, "MMM d, yyyy") : "Pick date"}
                                    </Button>
                                  </PopoverTrigger>
                                  <PopoverContent className="w-auto p-0 bg-popover border border-border" align="start">
                                    <Calendar
                                      mode="single"
                                      selected={recurrenceEndDate}
                                      onSelect={setRecurrenceEndDate}
                                      disabled={(date) => isBefore(date, startOfDay(new Date()))}
                                      initialFocus
                                    />
                                  </PopoverContent>
                                </Popover>
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="pt-3 border-t border-border flex gap-2">
                          <Button
                            size="sm"
                            type="button"
                            className="flex-1 h-8"
                            onClick={() => setRecurrencePopoverOpen(false)}
                          >
                            Done
                          </Button>
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                )}
              </div>
            </div>
          </div>

          <DialogFooter className="sticky bottom-0 bg-card pt-6 pb-2 mt-6 border-t border-border max-md:px-4 max-md:pb-4">
            <div className="flex gap-3 w-full sm:w-auto">
              <Button
                type="button"
                variant="outline"
                className="flex-1 sm:flex-none"
                onClick={() => setOpen(false)}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 sm:flex-none bg-workspace-accent text-white hover:bg-workspace-accent-hover shadow-sm"
                type="submit"
              >
                Add task
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
